import { useEffect, useState } from "react";
import Typewriter from 'typewriter-effect';
import Header from "../components/header";
import { JobObjectiveHelper, JobObjectiveModel, SignupModelHelper } from "../data/SignupModel";
import AmarelinhoService from "../services/AmarelinhoService";

import { CircularProgress } from '@mui/material';
import Stack from '@mui/material/Stack';
import InteractionModel, { InteractionAuthor } from '../data/InteractionModel';
import { NormalizedRoleDto } from '../data/dto/role.dto';
import { replaceSpecialCharacters } from '../functions/replace-special-characters';
import { useDebouncedValue } from '../hooks/use-debounce';
import { AmplitudeService } from '../services/AmplitudeService';

let timerError: NodeJS.Timeout | null = null;

export default function ObjetivosProfissionaisPesquisa(props: any) {

  const [typed, setTyped] = useState(false);
  const [jobFilter, setJobFilter] = useState('');
  const [jobList, setJobList] = useState<JobObjectiveModel[]>([]);
  const [normalizedRoles, setNormalizedRoles] = useState<NormalizedRoleDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState<JobObjectiveModel[]>(SignupModelHelper.get()?.objectives ?? []);
  const [interactions, setInteractions] = useState<InteractionModel[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const debouncedJobFilter = useDebouncedValue(jobFilter.trim(), 500);

  useEffect(() => {
    SignupModelHelper.checkLogin().then(async (userId) => {
      if (!userId) {
        document.location = '#/';
      }
    });

    // TagManager.dataLayer(dataLayerHelper.pagina('pesquisa', 'objetivos-profissionais'));
    AmplitudeService.track('Professional Goals Screen')

    setLoading(true);
    AmarelinhoService.getNormalizedRoles().then(normalizedRoles => {
      setLoading(false);
      if (!normalizedRoles) {
        setTimeout(() => {
          setInteractions([...interactions, {
            author: InteractionAuthor.AMARELINHO,
            text: 'Estou com problemas para buscar as opções de cargos. Favor tente mais tarde.'
          }]);
          setTimeout(() => {
            let chat = document.getElementsByClassName('chat')[0];
            chat.scrollTo(0, chat.scrollHeight);
          }, 500);
        }, 1000)
      } else {
        setNormalizedRoles(normalizedRoles);
      }
    })

  }, [])

  useEffect(() => {
    if (typed)
      document.getElementById('footer-input')?.focus();
  }, [typed]);

  function addJob(job: JobObjectiveModel) {
    if (selectedJobs.length >= 5) {
      setErrorMessage("Limite de 5 profissões atingido");
      if(timerError) clearTimeout(timerError);
      timerError = setTimeout(() => {
        setErrorMessage("");
      }, 5000);
    } else if (!selectedJobs.some(j => j.id === job.id)) {
      const newJobs = [...selectedJobs, job];
      setSelectedJobs(newJobs);
      SignupModelHelper.update({ objectives: newJobs });
    }
  }

  function removeJob(job: JobObjectiveModel) {
    const newJobs = selectedJobs.filter(j => j.id !== job.id);
    setSelectedJobs(newJobs);
    SignupModelHelper.update({ objectives: newJobs });
  }

  async function saveObjectives() {
    setSubmitted(true);
    setJobList([]);
    const signup = SignupModelHelper.get()!;
    const chat = [...interactions, {
      text: selectedJobs.map(j => j.label).join(', '),
      author: InteractionAuthor.CLIENTE
    }];
    setInteractions(chat);
    setLoading(true);
    const result = await AmarelinhoService.saveObjectives(signup);
    setLoading(false);
    if (result.success) {
      // TagManager.dataLayer(dataLayerHelper.formEnviado('form_objetivos-profissionais', signup.objectives.map(o => o.label).join(' ')));
      AmplitudeService.track('Professional Goals Submit', {roles: signup.objectives.map(o => o.label)})
      document.location = '#/dados-profissionais'
    } else {
      setTimeout(() => {
        setInteractions([...chat, {
          author: InteractionAuthor.AMARELINHO,
          text: 'Estou com problemas para salvar seu cadastro. Favor tente mais tarde.'
        }]);
        setTimeout(() => {
          let chat = document.getElementsByClassName('chat')[0];
          chat.scrollTo(0, chat.scrollHeight);
        }, 500);
      }, 1000)
    }
  }

  useEffect(() => {
    if (debouncedJobFilter.length > 0) {
      const fetchData = async () => {
        setLoading(true);
        const filterWithoutSpecialCharacters = replaceSpecialCharacters(debouncedJobFilter).toLowerCase();
        const jobs = normalizedRoles.filter((role) =>
          replaceSpecialCharacters(role.nome)
            .toLowerCase()
            .indexOf(filterWithoutSpecialCharacters) > -1
        ).sort((a, b) => {
          const aNome = replaceSpecialCharacters(a.nome).toLowerCase();
          const bNome = replaceSpecialCharacters(b.nome).toLowerCase();
          const aIndex = aNome.indexOf(filterWithoutSpecialCharacters);
          const bIndex = bNome.indexOf(filterWithoutSpecialCharacters);
          if (aIndex - bIndex === 0) {
            return aNome.localeCompare(bNome);
          }
          return aIndex - bIndex;
        }).slice(0, 20);

        setLoading(false);


        if (jobs) {
          const allJobs = JobObjectiveHelper.fromNormalizedRoles(jobs);
          setJobList(allJobs);
        }
      }
      fetchData();
    } else {
      setJobList([])
    }

  }, [debouncedJobFilter, normalizedRoles]);

  let objJobs = jobList.map((c: JobObjectiveModel) => {
    return <div className="professional-item" key={ c.id }>
      <button className="btn wrap btn-outline btn-full" data-id={ c.id } onClick={ () => addJob(c) }>{ c.label }</button>
    </div>;
  });


  return (
    <>
      <Header step={ 2 } total={ 2 } title="Seu objetivo profissional" previous="#/pretensao-salarial" finished={ false }></Header>

      <div className="chat-container">

        <div className="chat">
          <div className="container">
            <div className="row">
              <div className="col-12 d-flex flex-column align-items-stretch justify-content-between">
                <div className="chat-item">
                  <img src="images/ico-chat-amarelinho.svg" alt="Amarelinho" />
                  <div className="chat-item-content">
                    <Typewriter
                      onInit={ (typewriter) => {
                        typewriter.changeDelay(5).typeString('Quais cargos você tem interesse?<br>Pesquise pela profissão que tem interesse e selecione na lista de resultados as que mais fizerem sentido para você.<br><br>Exemplo de pesquisa: motori<br>Irá aparecer na listar MOTORISTA e AJUDANTE DE MOTORISTA, então você seleciona a profissão que preferir<br><br>Ah, você pode selecionar até 5 profissões')
                          .callFunction(() => {
                            setTyped(true)
                          })
                          .start();
                      } } />
                  </div>
                </div>

                {
                  interactions.map((interaction) => {
                    let objRet;

                    if (interaction.author === InteractionAuthor.AMARELINHO) {
                      objRet = <div className="chat-item" key={ interactions.indexOf(interaction) }>
                        <img src="images/ico-chat-amarelinho.svg" alt="Amarelinho" />
                        <div className="chat-item-content">
                          <Typewriter
                            onInit={ (typewriter) => {
                              typewriter.changeDelay(15).typeString(interaction.text)
                                .start();
                            } } />
                        </div>
                      </div>

                    } else if (interaction.author === InteractionAuthor.CLIENTE) {
                      objRet = <div className="chat-item chat-item-user" key={ interactions.indexOf(interaction) }>
                        <img src="images/ico-chat-user.svg" alt="Usuário" />
                        <div className="chat-item-content">
                          <p>{ interaction.text }</p>
                        </div>
                      </div>
                    }

                    return objRet;
                  })
                }
                { typed && !loading && !submitted && debouncedJobFilter.length > 0 && objJobs && objJobs.length === 0 &&
                  <div className="chat-item">
                    <div className="chat-item-content">
                      <div className="mt-5">
                        <p><strong>Nenhum resultado encontrado <br />para o termo pesquisado.</strong></p>
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
            { typed && !loading && objJobs &&
              <div className="professional-list">
                {
                  objJobs.map((objJob: any) => {
                    return objJob;
                  })
                }
              </div>
            }

          </div>
        </div>


        {
          errorMessage.length > 0 && (
            <p className="error-message" style={ { margin: 16 } }>{ errorMessage }</p>
          )
        }
        { !loading && selectedJobs.length > 0 && (
          <>
            <ul className="chips">
              { selectedJobs.map(j => (
                <li>
                  <div className="chip">{ j.label } <div className="chip-remove" onClick={ () => !loading && removeJob(j) }>X</div></div>
                </li>
              )) }
            </ul>
          </>
        ) }
        <footer className="footer-answer">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="footer-answer-holder d-flex align-items-center justify-content-between">
                  { loading && (
                    <Stack sx={ { color: '#6F5192' } } justifyContent="center" alignItems="center">
                      <CircularProgress color="inherit" />
                    </Stack>
                  ) }
                  { !loading && (
                    <>
                      <input type="text" id="footer-input" placeholder="Pesquise uma profissão aqui" onChange={ e => setJobFilter(e.currentTarget.value) } value={ jobFilter } />
                      <button className="btn-submit" onClick={ saveObjectives } disabled={ selectedJobs.length === 0 }>
                        <svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.0114286 20L24 10L0.0114286 0L0 7.77778L17.1429 10L0 12.2222L0.0114286 20Z"></path></svg>
                      </button>
                    </>
                  ) }
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>);
}
